import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button, TextButton } from '@xing-com/button';
import { Hyperlink } from '@xing-com/hyperlink';
import { Image } from '@xing-com/image';
import { Headline, BodyCopy, Hero } from '@xing-com/typography';

import ControlledInputBar from '../components/controlled-input-bar';
import ControlledRadioButtonGroup from '../components/controlled-radio-button-group';
import RegistrationButtonIframe from '../components/registration-button-iframe';
import type { BrazeCustomEvent } from '../consumers/prescreen-consumer/prescreen-consumer';
import { ALT_IMAGE_MISSING_EVENT_NAME } from '../events';

import { shouldReportMissingAlt } from './alt-text';

type StyledProps = {
  textalign?: string;
  hasTwoButtons?: boolean;
};

type FontWeightProps = {
  fontWeight?: 'bold' | 'regular';
};

type TargetProperty = {
  target?: '_self' | '_blank';
};

export const StyledBodyCopy = styled(BodyCopy)<StyledProps>`
  text-align: ${({ textalign }) => textalign || 'center'};
`;

export const StyledHeadline = styled(Headline)<StyledProps>`
  text-align: ${({ textalign }) => textalign || 'center'};
`;

const StyledHero = styled(Hero)<StyledProps>`
  text-align: ${({ textalign }) => textalign || 'center'};
`;

export const StyledSpan = styled.span<FontWeightProps>`
  font-weight: ${({ fontWeight }) => fontWeight};
`;

type StyledHyperlinkProps = FontWeightProps & TargetProperty;
export const StyledHyperlink = styled(Hyperlink)<StyledHyperlinkProps>`
  font-weight: ${({ fontWeight }) => fontWeight};
`;

const StyledButtonContainer = styled.div<StyledProps>`
  display: flex;
  justify-content: ${({ textalign, hasTwoButtons }) =>
    hasTwoButtons ? 'flex-end' : textalign || 'center'};
`;

const ImageContainer = styled(Image)<StyledProps>`
  margin-left: auto;
  margin-right: auto;
  max-width: 300px;
  padding-bottom: 20px;
`;

const StyledTextButton = styled(TextButton)`
  padding-right: 30px;
`;

// Helper component to handle image validation and side effects
const ValidatedImage = ({
  url,
  alt,
  className,
  sendBrazeCustomEvent,
  isSmallImage,
}: {
  url: string;
  alt?: string;
  className?: string;
  sendBrazeCustomEvent?: (payload: BrazeCustomEvent) => void;
  isSmallImage?: boolean;
}): JSX.Element | null => {
  const eventSent = useRef(false);
  const missingAlt = shouldReportMissingAlt(alt);

  useEffect(() => {
    if (missingAlt && !eventSent.current) {
      sendBrazeCustomEvent?.({ name: ALT_IMAGE_MISSING_EVENT_NAME });
      eventSent.current = true;
    }
  }, [missingAlt, sendBrazeCustomEvent]);

  if (missingAlt) return <div />;

  if (isSmallImage) {
    return <ImageContainer src={url} alt={alt} className={className} />;
  }

  return <Image src={url} alt={alt} className={className} />;
};

interface HeadlineStyle {
  size?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
  textAlign?: string;
}

interface HeadlineContent {
  type: 'headline';
  style?: HeadlineStyle;
  text: string;
}

interface HeroStyle {
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  textAlign?: string;
}

interface HeroContent {
  type: 'hero';
  style: HeroStyle;
  text: string;
}

interface ImageContent {
  type: 'image';
  url: string;
  alt: string;
}

interface SmallImageContent {
  type: 'smallImage';
  url: string;
  alt: string;
}

interface TextStyle {
  textAlign?: string;
  size?: 'small' | 'medium' | 'large' | 'xlarge' | 'xsmall';
  fontWeight?: 'regular' | 'bold';
  noMargin?: string;
}

interface ParagraphContent {
  type: 'paragraph';
  value: (TextContent | HyperlinkContent)[];
  style?: TextStyle;
}

interface TextContent {
  type: 'text';
  style?: TextStyle;
  text: string;
}

interface HyperlinkContent {
  type: 'hyperlink';
  url: string;
  linkText: string;
  target?: '_self' | '_blank';
  ariaLabel?: string;
  style?: FontWeightProps;
}

interface ConditionedHeadlineContent {
  type: 'conditionedHeadline';
  style?: TextStyle;
  variant1: string;
  variant2: string;
  variant3: string;
}

interface ButtonStyle {
  textAlign?: string;
}

export interface ButtonContent {
  type: 'button';
  action:
    | 'close'
    | 'next'
    | 'nextPage'
    | 'submit'
    | 'submitAndNext'
    | 'openURL'
    | 'customEvent'
    | 'fallback';
  brazeAttribute?: string;
  style?: ButtonStyle;
  url?: string;
  target?: '_self' | '_blank';
  buttonId?: string;
  disabled?: string;
  variant?:
    | 'text'
    | 'primary'
    | 'secondary'
    | 'overlay'
    | 'tertiary'
    | 'proBusiness'
    | 'premium'
    | 'proJobs';
  text: string;
  ariaLabel?: string;
  customEventPayload?: { name: string; properties: any };
}

interface RadioContent {
  type: 'radio';
  name: string;
  values: { value: string; label: string }[];
  orientation?: 'horizontal' | 'vertical';
}

interface TextFieldContent {
  type: 'textField';
  name: string;
  placeholder: string;
}

interface RegistrationButtonContent {
  type: 'registrationButtons';
  url?: string;
  id: string;
  numberButtons?: number;
  title?: string;
}

interface ButtonContainer {
  type: 'buttonContainer';
  value: ButtonContent[];
}

export interface HtmlSerializerProps {
  content:
    | HeadlineContent
    | HeroContent
    | ImageContent
    | SmallImageContent
    | ParagraphContent
    | HyperlinkContent
    | TextContent
    | ConditionedHeadlineContent
    | ButtonContent
    | RadioContent
    | TextFieldContent
    | RegistrationButtonContent
    | ButtonContainer;
  key?: string;
  onClose: () => void;
  onChange?: (name: string, value: string) => void;
  handleNextPage?: (attribute?: string) => void;
  onURLButtonClick?: (
    url: string,
    target?: '_self' | '_blank',
    buttonId?: string
  ) => void;
  textVariant?: 1 | 2 | 3;
  npsValue?: string | undefined;
  buttonColor?:
    | 'primary'
    | 'secondary'
    | 'overlay'
    | 'tertiary'
    | 'proBusiness'
    | 'premium'
    | 'proJobs';
  sendBrazeCustomEvent?: (payload: BrazeCustomEvent) => void;
  onFallback?: () => void;
}

const htmlSerializer = ({
  content,
  key,
  onClose,
  onChange,
  handleNextPage,
  onURLButtonClick,
  buttonColor,
  textVariant,
  npsValue,
  sendBrazeCustomEvent,
  onFallback,
}: HtmlSerializerProps): JSX.Element | null => {
  const hasTwoButtons =
    content.type === 'buttonContainer' ? content['value'].length > 1 : false;

  const buttonClickHandler = (button: ButtonContent): void => {
    // trigger custom event if payload is provided
    if (button.customEventPayload && sendBrazeCustomEvent) {
      sendBrazeCustomEvent(button.customEventPayload);
    }
    // Handle different button actions
    if (button.action === 'close') onClose?.();
    else if (button.action === 'next' || button.action === 'nextPage')
      handleNextPage?.();
    else if (button.action === 'submitAndNext') {
      handleNextPage?.(button.brazeAttribute);
    } else if (
      button.action === 'openURL' ||
      button.action === 'customEvent'
      // Deprecated action, adding customEventPayload as button property to handle custom events
    ) {
      if (button.url) {
        onURLButtonClick?.(
          button.url,
          button.target || '_blank',
          button.buttonId
        );
      } else {
        onClose?.();
      }
    } else if (button.action === 'fallback') {
      onFallback?.();
    } else {
      onClose?.();
    }
  };

  switch (content.type) {
    case 'headline':
      return (
        <StyledHeadline
          key={key}
          size={content.style?.size}
          textalign={content.style?.textAlign}
        >
          {content.text}
        </StyledHeadline>
      );
    case 'hero':
      return (
        <StyledHero
          key={key}
          size={content.style?.size}
          textalign={content.style?.textAlign}
        >
          {content.text}
        </StyledHero>
      );
    case 'image':
      return (
        <ValidatedImage
          key={key}
          url={content.url}
          alt={content.alt}
          sendBrazeCustomEvent={sendBrazeCustomEvent}
        />
      );
    case 'smallImage':
      return (
        <ValidatedImage
          key={key}
          url={content.url}
          alt={content.alt}
          sendBrazeCustomEvent={sendBrazeCustomEvent}
          isSmallImage
        />
      );
    case 'paragraph': {
      const children = content.value.map((paragraphContent, index) =>
        htmlSerializer({
          content: paragraphContent,
          key: `${key}-paragraph-content-${index}`,
          onClose,
          handleNextPage,
        })
      );

      return (
        <StyledBodyCopy
          key={key}
          textalign={content.style?.textAlign}
          size={content.style?.size}
          fontWeight={content.style?.fontWeight ?? 'regular'}
          noMargin={Boolean(content.style?.noMargin)}
        >
          {children}
        </StyledBodyCopy>
      );
    }
    case 'text':
      if (
        content.style?.textAlign ||
        content.style?.size ||
        content.style?.noMargin
      ) {
        return (
          <StyledBodyCopy
            key={key}
            textalign={content.style?.textAlign}
            size={content.style?.size}
            fontWeight={content.style?.fontWeight ?? 'regular'}
            noMargin={Boolean(content.style?.noMargin)}
          >
            {content.text}
          </StyledBodyCopy>
        );
      }
      return (
        <StyledSpan
          key={`${key}-span-content`}
          fontWeight={content.style?.fontWeight ?? 'regular'}
        >
          {content.text}
        </StyledSpan>
      );

    case 'hyperlink':
      return (
        <StyledHyperlink
          external={false}
          href={content.url}
          aria-label={content.ariaLabel}
          target={content.target || '_blank'}
          fontWeight={content.style?.fontWeight ?? 'regular'}
        >
          {content.linkText}
        </StyledHyperlink>
      );
    case 'conditionedHeadline':
      return (
        <StyledHeadline
          key={key}
          // @ts-expect-error i do not know why typescript yells here
          size={content.style?.size}
          textalign={content.style?.textAlign}
        >
          {content[`variant${textVariant || 1}`]}
        </StyledHeadline>
      );
    case 'buttonContainer': {
      const buttons = content.value; // ButtonContent[]
      const button1: ButtonContent | undefined = buttons[0];
      const button2: ButtonContent | undefined = buttons[1];

      return (
        <StyledButtonContainer key={key} hasTwoButtons={hasTwoButtons}>
          <StyledTextButton
            onClick={() => {
              buttonClickHandler(button1);
            }}
            aria-label={button1.ariaLabel || button1.text}
            size="medium"
          >
            {button1.text || <FormattedMessage id="BUTTON_CLOSE" />}
          </StyledTextButton>
          <Button
            onClick={() => {
              buttonClickHandler(button2);
            }}
            size="medium"
            aria-label={button2.ariaLabel || button2.text}
            variant={buttonColor || 'primary'}
          >
            {button2.text || <FormattedMessage id="BUTTON_FORWARD" />}
          </Button>
        </StyledButtonContainer>
      );
    }
    case 'button': {
      let disabled = false;

      if (npsValue === 'not set') {
        disabled = true;
      }

      return (
        <StyledButtonContainer
          key={key}
          textalign={content.style?.textAlign}
          hasTwoButtons={hasTwoButtons}
        >
          {content.variant === 'text' ? (
            <TextButton
              onClick={() => buttonClickHandler(content)}
              disabled={disabled}
              aria-label={content.ariaLabel || content.text}
            >
              {content.text}
            </TextButton>
          ) : (
            <Button
              onClick={() => buttonClickHandler(content)}
              variant={content.variant || 'primary'}
              disabled={disabled}
              aria-label={content.ariaLabel || content.text}
            >
              {content.text}
            </Button>
          )}
        </StyledButtonContainer>
      );
    }
    case 'radio':
      return (
        <ControlledRadioButtonGroup
          name={content.name}
          orientation={content.orientation}
          values={content.values}
          onChange={onChange}
          key={key}
        />
      );
    case 'textField':
      return (
        <ControlledInputBar
          name={content.name}
          onChange={onChange}
          placeholder={content.placeholder}
          key={key}
        />
      );
    case 'registrationButtons':
      return (
        <RegistrationButtonIframe
          url={content.url}
          id={content.id}
          numberButtons={content.numberButtons}
          title={content.title}
          key={key}
        ></RegistrationButtonIframe>
      );
    default:
      return null;
  }
};

export default htmlSerializer;
